import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../bizsuite-app';

/* ── Constants ── */
const LETTER_CATEGORIES = [
  'Appraisal Letter', 'Offer Letter', 'Experience Letter', 'Relieving Letter',
  'Confirmation Letter', 'Warning Letter', 'Promotion Letter', 'Other'
];

const FIELD_GROUPS = [
  {
    title: 'Employee Details',
    fields: [
      { tag: 'first_name', label: 'First Name' },
      { tag: 'last_name', label: 'Last Name' },
      { tag: 'full_name', label: 'Full Name' },
      { tag: 'emp_code', label: 'Employee Code' },
      { tag: 'gender', label: 'Gender' },
      { tag: 'date_of_birth', label: 'Date of Birth' },
      { tag: 'nationality', label: 'Nationality' },
      { tag: 'marital_status', label: 'Marital Status' },
      { tag: 'blood_group', label: 'Blood Group' },
      { tag: 'designation', label: 'Designation' },
      { tag: 'department', label: 'Department' },
      { tag: 'employment_type', label: 'Employment Type' },
      { tag: 'location', label: 'Location' },
      { tag: 'status', label: 'Status' },
      { tag: 'date_of_joining', label: 'Date of Joining' },
      { tag: 'date_of_offer', label: 'Date of Offer' },
      { tag: 'probation_status', label: 'Probation Status' },
      { tag: 'reporting_manager', label: 'Reporting Manager' },
      { tag: 'reporting_manager_code', label: 'Reporting Mgr Code' },
      { tag: 'reporting_manager_designation', label: 'Reporting Mgr Designation' },
    ]
  },
  {
    title: 'Contact Details',
    fields: [
      { tag: 'email', label: 'Official Email' },
      { tag: 'personal_email', label: 'Personal Email' },
      { tag: 'phone', label: 'Phone' },
      { tag: 'alternate_phone', label: 'Alternate Phone' },
      { tag: 'current_address', label: 'Current Address' },
      { tag: 'home_location', label: 'Home Location' },
      { tag: 'local_address', label: 'Local Address' },
    ]
  },
  {
    title: 'Compensation',
    fields: [
      { tag: 'offer_ctc_annual', label: 'Annual CTC' },
      { tag: 'gross_pa_fixed', label: 'Gross PA (Fixed)' },
      { tag: 'gross_pm_fx_var', label: 'Gross PM (Fx+Var)' },
      { tag: 'basic_salary', label: 'Basic Salary' },
      { tag: 'hra', label: 'HRA' },
      { tag: 'statutory_bonus', label: 'Statutory Bonus' },
      { tag: 'lta', label: 'LTA' },
      { tag: 'personal_allowance', label: 'Personal Allowance' },
      { tag: 'variable_pay', label: 'Variable Pay' },
      { tag: 'net_pm', label: 'Net PM' },
      { tag: 'company_pf', label: 'Company PF' },
      { tag: 'employee_pf', label: 'Employee PF' },
    ]
  },
  {
    title: 'Banking & Statutory',
    fields: [
      { tag: 'bank_name', label: 'Bank Name' },
      { tag: 'bank_account_number', label: 'Bank Account No.' },
      { tag: 'ifsc_code', label: 'IFSC Code' },
      { tag: 'pan_number', label: 'PAN Number' },
      { tag: 'aadhaar_number', label: 'Aadhaar Number' },
      { tag: 'uan_number', label: 'UAN Number' },
      { tag: 'pf_account_no', label: 'PF Account No.' },
      { tag: 'esic_number', label: 'ESIC Number' },
    ]
  },
  {
    title: 'Office Details',
    fields: [
      { tag: 'office_name', label: 'Office Name' },
      { tag: 'office_address', label: 'Office Address' },
      { tag: 'office_city', label: 'Office City' },
      { tag: 'office_country', label: 'Office Country' },
    ]
  },
  {
    title: 'Company Details',
    fields: [
      { tag: 'company_name', label: 'Company Name' },
      { tag: 'company_address', label: 'Company Address' },
      { tag: 'company_city', label: 'Company City' },
      { tag: 'company_state', label: 'Company State' },
      { tag: 'company_country', label: 'Company Country' },
      { tag: 'company_pincode', label: 'Company Pincode' },
    ]
  },
  {
    title: 'Date & Meta',
    fields: [
      { tag: 'today_date', label: 'Today\'s Date' },
      { tag: 'current_year', label: 'Current Year' },
    ]
  },
  {
    title: 'Separation',
    fields: [
      { tag: 'exit_date', label: 'Exit Date' },
      { tag: 'reason_for_exit', label: 'Reason for Exit' },
      { tag: 'last_working_date', label: 'Last Working Date' },
    ]
  },
  {
    title: 'Custom Fields',
    fields: [
      { tag: 'authorized_signatory', label: 'Authorized Signatory' },
      { tag: 'signatory_designation', label: 'Signatory Designation' },
      { tag: 'effective_date', label: 'Effective Date' },
    ]
  },
];

/* ── Font families for the editor ── */
const FONT_FAMILIES = [
  'Arial', 'Times New Roman', 'Georgia', 'Verdana', 'Courier New',
  'Trebuchet MS', 'Tahoma', 'Garamond', 'Palatino', 'Calibri',
];

/* ── CDN loader helper ── */
function loadScript(src, globalVar) {
  return new Promise((resolve) => {
    if (window[globalVar]) { resolve(window[globalVar]); return; }
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      const check = setInterval(() => {
        if (window[globalVar]) { clearInterval(check); resolve(window[globalVar]); }
      }, 100);
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve(window[globalVar]);
    document.head.appendChild(script);
  });
}

/* ── Quill + Mammoth CDN loaders ── */
let quillLoaded = false;
function loadQuill() {
  return new Promise((resolve) => {
    if (quillLoaded && window.Quill) { resolve(window.Quill); return; }
    // CSS
    if (!document.querySelector('link[href*="quill.snow"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.snow.css';
      document.head.appendChild(link);
    }
    loadScript('https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.js', 'Quill').then((Q) => {
      quillLoaded = true;
      resolve(Q);
    });
  });
}

function loadMammoth() {
  return loadScript('https://cdn.jsdelivr.net/npm/mammoth@1.8.0/mammoth.browser.min.js', 'mammoth');
}

/* ── Styles ── */
const S = {
  page: { display: 'flex', height: 'calc(100vh - 80px)', gap: 0, overflow: 'hidden' },
  leftPanel: {
    width: 280, minWidth: 280, background: '#0F172A', borderRight: '1px solid rgba(255,255,255,0.08)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  leftHeader: {
    padding: '16px 16px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  leftBody: {
    flex: 1, overflowY: 'auto', padding: '8px 12px',
  },
  rightPanel: {
    flex: 1, display: 'flex', flexDirection: 'column', background: '#1E293B', overflow: 'hidden',
  },
  toolbar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    padding: '10px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)',
    background: '#0F172A', flexWrap: 'wrap',
  },
  editorWrap: {
    flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
    overflow: 'auto', padding: 24, background: '#334155',
  },
  editorPage: {
    width: 816, minHeight: 1056, background: '#FFFFFF', boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
    borderRadius: 4, position: 'relative', display: 'flex', flexDirection: 'column',
  },
  groupTitle: {
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px',
    color: '#64748B', padding: '10px 4px 4px', marginTop: 4,
  },
  fieldChip: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', marginBottom: 3,
    borderRadius: 5, fontSize: 12.5, cursor: 'grab', color: '#E2E8F0',
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
    transition: 'all 0.15s',
    userSelect: 'none',
  },
  fieldTag: {
    fontSize: 10, color: '#60A5FA', fontFamily: 'monospace', marginLeft: 'auto',
  },
  templateList: {
    padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.06)',
    maxHeight: 200, overflowY: 'auto',
  },
  input: {
    width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 5, padding: '7px 10px', color: '#E2E8F0', fontSize: 13, outline: 'none',
  },
  select: {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 5, padding: '7px 10px', color: '#E2E8F0', fontSize: 13, outline: 'none',
  },
  btn: (bg, color, border) => ({
    background: bg, color, border: border || 'none', padding: '7px 16px',
    borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
  }),
  /* Secondary toolbar buttons */
  secBtn: {
    background: '#fff', border: '1px solid #CBD5E1', borderRadius: 4,
    padding: '5px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
    color: '#334155', display: 'flex', alignItems: 'center', gap: 5,
    whiteSpace: 'nowrap',
  },
};

/* ── Custom Quill CSS overrides ── */
const QUILL_OVERRIDES = `
  /* ── Toolbar: sticky inside .letter-editor ── */
  .letter-editor .ql-toolbar.ql-snow {
    border: none; border-bottom: 1px solid #e2e8f0; background: #f8fafc;
    padding: 6px 12px; position: sticky; top: 0; z-index: 10;
  }
  .letter-editor .ql-container.ql-snow { border: none; font-size: 12pt; }
  .letter-editor .ql-editor {
    padding: 72px 72px 72px 72px; min-height: 980px; line-height: 1.6; color: #1e293b;
  }
  .letter-editor .ql-editor p { margin-bottom: 4px; }
  .letter-editor .ql-editor h1 { font-size: 22pt; color: #1b3a5c; margin-bottom: 8px; }
  .letter-editor .ql-editor h2 { font-size: 16pt; color: #2e5a88; margin-bottom: 6px; }
  .letter-editor .ql-editor h3 { font-size: 13pt; color: #334155; margin-bottom: 4px; }

  /* Placeholder tags */
  .letter-editor .placeholder-tag {
    background: #DBEAFE; color: #1D4ED8; padding: 2px 6px; border-radius: 3px;
    font-family: monospace; font-size: 11px; cursor: default; white-space: nowrap;
    border: 1px solid #93C5FD;
  }

  /* Page break */
  .letter-editor .ql-editor .page-break-blot {
    display: block; width: 100%; border: none; border-top: 2px dashed #94A3B8;
    margin: 24px 0; padding: 0; height: 0; position: relative;
  }
  .letter-editor .ql-editor .page-break-blot::after {
    content: 'PAGE BREAK'; position: absolute; top: -10px; left: 50%; transform: translateX(-50%);
    background: #fff; padding: 0 12px; font-size: 9px; color: #94A3B8; letter-spacing: 1px; font-weight: 600;
  }

  /* Document header / footer areas */
  .letter-editor .ql-editor .doc-header-area {
    border-bottom: 2px solid #CBD5E1; padding-bottom: 16px; margin-bottom: 20px;
  }
  .letter-editor .ql-editor .doc-footer-area {
    border-top: 2px solid #CBD5E1; padding-top: 12px; margin-top: 30px;
  }

  /* Tables */
  .letter-editor .ql-editor table { border-collapse: collapse; width: 100%; margin: 12px 0; }
  .letter-editor .ql-editor table td, .letter-editor .ql-editor table th {
    border: 1px solid #CBD5E1; padding: 8px 12px; font-size: 11pt; text-align: left;
  }
  .letter-editor .ql-editor table th { background: #F1F5F9; font-weight: 600; color: #334155; }

  /* Logo images — resizable */
  .letter-editor .ql-editor img.doc-logo {
    max-width: 100%; cursor: pointer; border: 1px solid transparent;
    transition: border .15s;
  }
  .letter-editor .ql-editor img.doc-logo:hover { border: 1px solid #3B82F6; }

  /* Font family Quill whitelist */
  .ql-snow .ql-picker.ql-font { width: 140px; }
  .ql-snow .ql-picker.ql-font .ql-picker-label::before,
  .ql-snow .ql-picker.ql-font .ql-picker-item::before { content: attr(data-value) !important; }

  .ql-font-arial { font-family: Arial, Helvetica, sans-serif; }
  .ql-font-times-new-roman { font-family: 'Times New Roman', Times, serif; }
  .ql-font-georgia { font-family: Georgia, serif; }
  .ql-font-verdana { font-family: Verdana, Geneva, sans-serif; }
  .ql-font-courier-new { font-family: 'Courier New', Courier, monospace; }
  .ql-font-trebuchet-ms { font-family: 'Trebuchet MS', Helvetica, sans-serif; }
  .ql-font-tahoma { font-family: Tahoma, Geneva, sans-serif; }
  .ql-font-garamond { font-family: Garamond, serif; }
  .ql-font-palatino { font-family: 'Palatino Linotype', 'Book Antiqua', Palatino, serif; }
  .ql-font-calibri { font-family: Calibri, 'Segoe UI', sans-serif; }

  /* Font size Quill */
  .ql-snow .ql-picker.ql-size { width: 70px; }
  .ql-size-8pt { font-size: 8pt; }
  .ql-size-9pt { font-size: 9pt; }
  .ql-size-10pt { font-size: 10pt; }
  .ql-size-11pt { font-size: 11pt; }
  .ql-size-12pt { font-size: 12pt; }
  .ql-size-14pt { font-size: 14pt; }
  .ql-size-16pt { font-size: 16pt; }
  .ql-size-18pt { font-size: 18pt; }
  .ql-size-20pt { font-size: 20pt; }
  .ql-size-24pt { font-size: 24pt; }
  .ql-size-28pt { font-size: 28pt; }
  .ql-size-36pt { font-size: 36pt; }

  /* Secondary toolbar */
  .letter-sec-toolbar {
    display: flex; align-items: center; gap: 6; padding: 6px 12px;
    background: #f1f5f9; border-bottom: 1px solid #e2e8f0;
    position: sticky; top: 43px; z-index: 9; flex-wrap: wrap;
  }
  .letter-sec-toolbar button:hover { background: #e2e8f0 !important; }

  /* Chips hover */
  .left-panel-chip:hover { background: rgba(59,130,246,0.12) !important; border-color: rgba(59,130,246,0.3) !important; }
  .left-panel-chip:active { cursor: grabbing !important; }
  .template-item:hover { background: rgba(255,255,255,0.06) !important; }

  /* Modal overlay */
  .lb-modal-overlay {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.5); z-index: 1000;
    display: flex; align-items: center; justify-content: center;
  }
  .lb-modal {
    background: #fff; border-radius: 12px; padding: 24px 28px;
    max-width: 480px; width: 90%; box-shadow: 0 20px 60px rgba(0,0,0,0.3);
    color: #1e293b;
  }
  .lb-modal h3 { margin: 0 0 16px; font-size: 17px; color: #1e293b; }
  .lb-modal label { display: block; margin-bottom: 12px; font-size: 13px; color: #475569; font-weight: 500; }
  .lb-modal label select, .lb-modal label input {
    display: block; width: 100%; margin-top: 4px; padding: 7px 10px;
    border: 1px solid #CBD5E1; border-radius: 6px; font-size: 13px; color: #1e293b;
  }
  .lb-modal .btn-row { display: flex; gap: 8px; justify-content: flex-end; margin-top: 18px; }
  .lb-modal .btn-row button {
    padding: 8px 18px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer;
  }
`;

export default function LetterBuilder() {
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  const [quillReady, setQuillReady] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [category, setCategory] = useState('Appraisal Letter');
  const [saving, setSaving] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [fieldSearch, setFieldSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  // Header/Footer modal state
  const [hfModal, setHfModal] = useState(null); // null | 'header' | 'footer'
  const [hfLogoData, setHfLogoData] = useState(null); // base64
  const [hfLogoSize, setHfLogoSize] = useState('120'); // px width
  const [hfLogoAlign, setHfLogoAlign] = useState('left'); // left | center | right
  const [hfCompanyName, setHfCompanyName] = useState(true);
  const [hfAddress, setHfAddress] = useState(true);
  const [hfDate, setHfDate] = useState(true);
  const [hfCustomText, setHfCustomText] = useState('');

  // Load Quill
  useEffect(() => {
    loadQuill().then((Quill) => {
      // Register font families
      const Font = Quill.import('attributors/class/font');
      Font.whitelist = FONT_FAMILIES.map(f => f.toLowerCase().replace(/\s+/g, '-'));
      Quill.register(Font, true);

      // Register font sizes
      const Size = Quill.import('attributors/style/size');
      Size.whitelist = ['8pt','9pt','10pt','11pt','12pt','14pt','16pt','18pt','20pt','24pt','28pt','36pt'];
      Quill.register(Size, true);

      // Register custom placeholder blot (inline)
      const Inline = Quill.import('blots/inline');
      class PlaceholderBlot extends Inline {
        static create(value) {
          const node = super.create();
          node.setAttribute('data-placeholder', value);
          node.setAttribute('contenteditable', 'false');
          node.classList.add('placeholder-tag');
          node.textContent = `{${value}}`;
          return node;
        }
        static value(node) {
          return node.getAttribute('data-placeholder');
        }
        static formats(node) {
          return node.getAttribute('data-placeholder');
        }
      }
      PlaceholderBlot.blotName = 'placeholder';
      PlaceholderBlot.tagName = 'span';
      PlaceholderBlot.className = 'placeholder-tag';
      Quill.register(PlaceholderBlot, true);

      // Register page-break blot (block embed)
      const BlockEmbed = Quill.import('blots/block/embed');
      class PageBreakBlot extends BlockEmbed {
        static create() {
          const node = super.create();
          node.classList.add('page-break-blot');
          node.setAttribute('contenteditable', 'false');
          return node;
        }
      }
      PageBreakBlot.blotName = 'pagebreak';
      PageBreakBlot.tagName = 'hr';
      PageBreakBlot.className = 'page-break-blot';
      Quill.register(PageBreakBlot, true);

      if (editorRef.current && !quillRef.current) {
        quillRef.current = new Quill(editorRef.current, {
          theme: 'snow',
          modules: {
            toolbar: {
              container: [
                [{ font: Font.whitelist }],
                [{ size: Size.whitelist }],
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ color: [] }, { background: [] }],
                [{ align: [] }],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ indent: '-1' }, { indent: '+1' }],
                ['blockquote', 'image', 'link'],
                ['clean'],
              ],
            },
          },
          placeholder: 'Start composing your letter here...\n\nDrag employee fields from the left panel or click them to insert placeholders.',
        });

        // Fix font dropdown labels
        setTimeout(() => {
          const editor = editorRef.current?.closest('.letter-editor');
          if (!editor) return;
          // Set font picker labels to readable names
          editor.querySelectorAll('.ql-font .ql-picker-item').forEach(item => {
            const val = item.getAttribute('data-value');
            if (val) {
              item.style.fontFamily = val.replace(/-/g, ' ');
            }
          });
          // Set size picker labels
          editor.querySelectorAll('.ql-size .ql-picker-item').forEach(item => {
            const val = item.getAttribute('data-value');
            if (!val) {
              item.setAttribute('data-value', '12pt');
            }
          });
        }, 200);

        setQuillReady(true);
      }
    });
  }, []);

  // Inject custom CSS
  useEffect(() => {
    const id = 'letter-builder-styles';
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = QUILL_OVERRIDES;
      document.head.appendChild(style);
    }
  }, []);

  // Fetch existing templates
  const fetchTemplates = useCallback(async () => {
    try {
      const res = await apiFetch('/api/hr-letters');
      if (res.ok) {
        const data = await res.json();
        setSavedTemplates(data.templates || []);
      }
    } catch (e) { /* ignore */ }
    setLoadingTemplates(false);
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // Insert placeholder into editor
  function insertPlaceholder(tag) {
    const quill = quillRef.current;
    if (!quill) return;
    quill.focus();
    const range = quill.getSelection(true);
    quill.insertEmbed(range.index, 'placeholder', tag, 'user');
    quill.insertText(range.index + 1, ' ', 'user');
    quill.setSelection(range.index + 2, 0);
  }

  // Handle drag start
  function handleDragStart(e, tag) {
    e.dataTransfer.setData('text/plain', `{${tag}}`);
    e.dataTransfer.setData('application/x-placeholder', tag);
    e.dataTransfer.effectAllowed = 'copy';
  }

  // Handle drop on editor
  useEffect(() => {
    if (!quillReady || !quillRef.current) return;
    const quill = quillRef.current;
    const editorEl = quill.root;

    function handleDrop(e) {
      const tag = e.dataTransfer.getData('application/x-placeholder');
      if (!tag) return;
      e.preventDefault();
      e.stopPropagation();

      const doc = editorEl.ownerDocument;
      let range;
      if (doc.caretRangeFromPoint) {
        range = doc.caretRangeFromPoint(e.clientX, e.clientY);
      } else if (doc.caretPositionFromPoint) {
        const pos = doc.caretPositionFromPoint(e.clientX, e.clientY);
        range = doc.createRange();
        range.setStart(pos.offsetNode, pos.offset);
      }

      if (range) {
        const sel = doc.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }

      setTimeout(() => {
        const sel = quill.getSelection(true);
        if (sel) {
          quill.insertEmbed(sel.index, 'placeholder', tag, 'user');
          quill.insertText(sel.index + 1, ' ', 'user');
          quill.setSelection(sel.index + 2, 0);
        }
      }, 0);
    }

    function handleDragOver(e) {
      if (e.dataTransfer.types.includes('application/x-placeholder')) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }
    }

    editorEl.addEventListener('drop', handleDrop);
    editorEl.addEventListener('dragover', handleDragOver);
    return () => {
      editorEl.removeEventListener('drop', handleDrop);
      editorEl.removeEventListener('dragover', handleDragOver);
    };
  }, [quillReady]);

  // ─── Secondary toolbar actions ───

  function handlePageBreak() {
    const quill = quillRef.current;
    if (!quill) return;
    const range = quill.getSelection(true);
    quill.insertEmbed(range.index, 'pagebreak', true, 'user');
    quill.setSelection(range.index + 1, 0);
  }

  function handleInsertTable() {
    const quill = quillRef.current;
    if (!quill) return;
    const rows = prompt('Number of rows:', '3');
    const cols = prompt('Number of columns:', '3');
    if (!rows || !cols) return;
    const r = parseInt(rows, 10);
    const c = parseInt(cols, 10);
    if (isNaN(r) || isNaN(c) || r < 1 || c < 1 || r > 20 || c > 10) return;
    const range = quill.getSelection(true);
    let tableHtml = '<table><thead><tr>';
    for (let j = 0; j < c; j++) tableHtml += `<th>Header ${j + 1}</th>`;
    tableHtml += '</tr></thead><tbody>';
    for (let i = 0; i < r; i++) {
      tableHtml += '<tr>';
      for (let j = 0; j < c; j++) tableHtml += '<td>&nbsp;</td>';
      tableHtml += '</tr>';
    }
    tableHtml += '</tbody></table>';
    quill.clipboard.dangerouslyPasteHTML(range.index, tableHtml, 'user');
  }

  // ─── Header/Footer modal ───

  function openHeaderModal() {
    setHfModal('header');
    setHfLogoData(null);
    setHfLogoSize('120');
    setHfLogoAlign('left');
    setHfCompanyName(true);
    setHfAddress(true);
    setHfDate(true);
    setHfCustomText('');
  }

  function openFooterModal() {
    setHfModal('footer');
    setHfLogoData(null);
    setHfLogoSize('80');
    setHfLogoAlign('center');
    setHfCompanyName(true);
    setHfAddress(true);
    setHfDate(false);
    setHfCustomText('');
  }

  function handleLogoFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setHfLogoData(reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  function insertHeaderFooter() {
    const quill = quillRef.current;
    if (!quill) return;
    const isHeader = hfModal === 'header';
    const areaClass = isHeader ? 'doc-header-area' : 'doc-footer-area';
    const alignStyle = `text-align:${hfLogoAlign};`;

    let html = `<div class="${areaClass}" style="${alignStyle}">`;

    // Logo
    if (hfLogoData) {
      html += `<img class="doc-logo" src="${hfLogoData}" alt="Logo" style="width:${hfLogoSize}px;display:inline-block;" />`;
    }

    // Company name
    if (hfCompanyName) {
      html += `<h2 style="${alignStyle}">{company_name}</h2>`;
    }

    // Address
    if (hfAddress) {
      html += `<p style="color:#64748B;font-size:10pt;${alignStyle}">{company_address}</p>`;
    }

    // Date (usually for header only)
    if (hfDate) {
      html += `<p style="color:#64748B;font-size:10pt;${alignStyle}">Date: {today_date}</p>`;
    }

    // Custom text
    if (hfCustomText.trim()) {
      html += `<p style="font-size:10pt;${alignStyle}">${hfCustomText.trim()}</p>`;
    }

    html += '</div>';

    if (isHeader) {
      // Insert at beginning
      quill.clipboard.dangerouslyPasteHTML(0, html + '<p><br></p>', 'user');
    } else {
      // Insert at end
      const len = quill.getLength();
      quill.clipboard.dangerouslyPasteHTML(len - 1, '<p><br></p>' + html, 'user');
    }

    setHfModal(null);
    setStatusMsg({ type: 'success', text: `${isHeader ? 'Header' : 'Footer'} inserted. You can edit it directly in the document.` });
  }

  function handleInsertLogo() {
    // Quick logo insertion into current cursor position
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = 'image/jpeg,image/png,image/webp,image/gif,image/svg+xml';
    inp.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const quill = quillRef.current;
        if (!quill) return;
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, 'image', reader.result, 'user');
        quill.setSelection(range.index + 1, 0);
        setStatusMsg({ type: 'info', text: 'Logo inserted. Tip: Use Header/Footer buttons for full letterhead with logo positioning.' });
      };
      reader.readAsDataURL(file);
    };
    inp.click();
  }

  // Convert Quill HTML to clean HTML with {placeholders} for .docx generation
  function getTemplateHtml() {
    if (!quillRef.current) return '';
    let html = quillRef.current.root.innerHTML;
    // Replace placeholder blots with {tag} text
    html = html.replace(/<span[^>]*class="placeholder-tag"[^>]*data-placeholder="([^"]*)"[^>]*>[^<]*<\/span>/g, '{$1}');
    // Replace page-break blots with page-break div for docx
    html = html.replace(/<hr[^>]*class="page-break-blot"[^>]*\/?>/g,
      '<div style="page-break-after: always;"><br/></div>');
    return html;
  }

  // Save as template
  async function handleSave() {
    if (!quillRef.current) return;
    if (!templateName.trim()) {
      setStatusMsg({ type: 'error', text: 'Please enter a template name' });
      return;
    }

    const html = getTemplateHtml();
    if (!html || html === '<p><br></p>') {
      setStatusMsg({ type: 'error', text: 'Template content is empty' });
      return;
    }

    setSaving(true);
    setStatusMsg(null);

    try {
      const res = await apiFetch('/api/hr-letters/save-builder', {
        method: 'POST',
        body: JSON.stringify({
          name: templateName.trim(),
          category,
          html,
          templateId: editingId || undefined,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setStatusMsg({ type: 'success', text: `Template "${templateName}" saved successfully!` });
        setEditingId(data.template?.id || null);
        fetchTemplates();
      } else {
        const data = await res.json();
        setStatusMsg({ type: 'error', text: data.error || 'Failed to save template' });
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Network error. Please try again.' });
    }
    setSaving(false);
  }

  // Load template into editor
  async function handleLoadTemplate(tpl) {
    if (!quillRef.current) return;
    try {
      const res = await apiFetch(`/api/hr-letters/builder-content/${tpl.id}`);
      if (res.ok) {
        const data = await res.json();
        const html = data.html || '';
        const processedHtml = html.replace(/\{([a-z_]+)\}/g,
          '<span class="placeholder-tag" data-placeholder="$1" contenteditable="false">{$1}</span>'
        );
        quillRef.current.root.innerHTML = processedHtml;
        setTemplateName(tpl.name);
        setCategory(tpl.category || 'Other');
        setEditingId(tpl.id);
        setStatusMsg({ type: 'info', text: `Loaded "${tpl.name}" for editing` });
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Failed to load template' });
    }
  }

  function handleNewTemplate() {
    if (quillRef.current) {
      quillRef.current.setText('');
    }
    setTemplateName('');
    setCategory('Appraisal Letter');
    setEditingId(null);
    setStatusMsg(null);
  }

  // Import .docx file
  async function handleImportDocx(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setStatusMsg({ type: 'info', text: `Importing "${file.name}"...` });
    try {
      const mammoth = await loadMammoth();
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });

      if (result.value) {
        const processedHtml = result.value.replace(/\{([a-z_]+)\}/g,
          '<span class="placeholder-tag" data-placeholder="$1" contenteditable="false">{$1}</span>'
        );
        quillRef.current.root.innerHTML = processedHtml;
        setTemplateName(file.name.replace(/\.docx$/i, ''));
        setEditingId(null);
        setStatusMsg({
          type: 'success',
          text: `Imported "${file.name}" — now add placeholders by dragging fields from the left panel, then save.`
            + (result.messages.length ? ` (${result.messages.length} warnings)` : '')
        });
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: `Import failed: ${err.message}` });
    }
    setImporting(false);
    e.target.value = '';
  }

  // Delete a saved template
  async function handleDeleteTemplate(e, tplId) {
    e.stopPropagation();
    if (!window.confirm('Delete this template permanently?')) return;
    try {
      const res = await apiFetch(`/api/hr-letters/${tplId}`, { method: 'DELETE' });
      if (res.ok) {
        setSavedTemplates(prev => prev.filter(t => t.id !== tplId));
        if (editingId === tplId) {
          handleNewTemplate();
        }
        setStatusMsg({ type: 'success', text: 'Template deleted' });
      } else {
        setStatusMsg({ type: 'error', text: 'Failed to delete template' });
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Delete error' });
    }
  }

  // Filter fields by search
  const filteredGroups = FIELD_GROUPS.map(g => ({
    ...g,
    fields: g.fields.filter(f =>
      !fieldSearch || f.label.toLowerCase().includes(fieldSearch.toLowerCase()) ||
      f.tag.toLowerCase().includes(fieldSearch.toLowerCase())
    )
  })).filter(g => g.fields.length > 0);

  return (
    <div style={S.page}>
      {/* ── Left Panel: Employee Fields ── */}
      <div style={S.leftPanel}>
        <div style={S.leftHeader}>
          <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: '#F1F5F9' }}>
            Employee Fields
          </h3>
          <p style={{ margin: '0 0 10px', fontSize: 11, color: '#64748B' }}>
            Drag or click fields to insert placeholders
          </p>
          <input
            type="text"
            placeholder="Search fields..."
            value={fieldSearch}
            onChange={e => setFieldSearch(e.target.value)}
            style={S.input}
          />
        </div>

        <div style={S.leftBody}>
          {filteredGroups.map(group => (
            <div key={group.title}>
              <div style={S.groupTitle}>{group.title}</div>
              {group.fields.map(field => (
                <div
                  key={field.tag}
                  className="left-panel-chip"
                  style={S.fieldChip}
                  draggable
                  onDragStart={e => handleDragStart(e, field.tag)}
                  onClick={() => insertPlaceholder(field.tag)}
                  title={`Click or drag to insert {${field.tag}}`}
                >
                  <span>{field.label}</span>
                  <span style={S.fieldTag}>{`{${field.tag}}`}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Saved templates */}
        <div style={S.templateList}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#64748B', marginBottom: 6 }}>
            Saved Templates ({savedTemplates.length})
          </div>
          {loadingTemplates ? (
            <div style={{ fontSize: 12, color: '#64748B', padding: 8 }}>Loading...</div>
          ) : savedTemplates.length === 0 ? (
            <div style={{ fontSize: 12, color: '#64748B', padding: 8 }}>No templates yet</div>
          ) : (
            savedTemplates.map(t => (
              <div
                key={t.id}
                className="template-item"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '6px 8px', borderRadius: 4, cursor: 'pointer', marginBottom: 2,
                  fontSize: 12, color: '#CBD5E1', background: editingId === t.id ? 'rgba(59,130,246,0.15)' : 'transparent',
                  borderLeft: editingId === t.id ? '2px solid #3B82F6' : '2px solid transparent',
                }}
              >
                <div onClick={() => handleLoadTemplate(t)} style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                  <div style={{ fontSize: 10, color: '#64748B' }}>
                    {t.category}
                    {t.html_content ? '' : ' (file upload)'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4, marginLeft: 4, flexShrink: 0 }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleLoadTemplate(t); }}
                    title="Edit template"
                    style={{ background: 'none', border: 'none', color: '#60A5FA', cursor: 'pointer', padding: '2px 4px', fontSize: 11 }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => handleDeleteTemplate(e, t.id)}
                    title="Delete template"
                    style={{ background: 'none', border: 'none', color: '#F87171', cursor: 'pointer', padding: '2px 4px', fontSize: 11 }}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Right Panel: Editor ── */}
      <div style={S.rightPanel}>
        {/* Top toolbar: template name, category, action buttons */}
        <div style={S.toolbar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Template name..."
              value={templateName}
              onChange={e => setTemplateName(e.target.value)}
              style={{ ...S.input, maxWidth: 240 }}
            />
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              style={{ ...S.select, minWidth: 160 }}
            >
              {LETTER_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={handleNewTemplate} style={S.btn('rgba(255,255,255,0.06)', '#CBD5E1', '1px solid rgba(255,255,255,0.12)')}>
              New
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              style={S.btn('rgba(59,130,246,0.12)', '#60A5FA', '1px solid rgba(59,130,246,0.25)')}
            >
              {importing ? 'Importing...' : 'Import .docx'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".docx"
              onChange={handleImportDocx}
              style={{ display: 'none' }}
            />
            <button
              onClick={handleSave}
              disabled={saving}
              style={S.btn('rgba(34,197,94,0.15)', '#4ADE80', '1px solid rgba(34,197,94,0.3)')}
            >
              {saving ? 'Saving...' : (editingId ? 'Update Template' : 'Save Template')}
            </button>
          </div>
        </div>

        {/* Status message */}
        {statusMsg && (
          <div style={{
            padding: '8px 20px', fontSize: 13,
            background: statusMsg.type === 'error' ? 'rgba(220,38,38,0.1)' : statusMsg.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(59,130,246,0.1)',
            color: statusMsg.type === 'error' ? '#F87171' : statusMsg.type === 'success' ? '#4ADE80' : '#60A5FA',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}>
            {statusMsg.text}
            <button onClick={() => setStatusMsg(null)} style={{ float: 'right', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16 }}>×</button>
          </div>
        )}

        {/* Editor area (scrollable — toolbar sticks inside) */}
        <div style={S.editorWrap}>
          <div style={S.editorPage} className="letter-editor">
            {/* Quill mounts its own toolbar + container here */}
            <div ref={editorRef} />

            {/* Secondary toolbar (after Quill toolbar — rendered once Quill is ready) */}
            {quillReady && (
              <div className="letter-sec-toolbar" style={{
                position: 'sticky', top: 43, zIndex: 9,
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 12px', background: '#f1f5f9',
                borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap',
              }}>
                <button onClick={openHeaderModal} style={S.secBtn} title="Insert letterhead / document header">
                  <span style={{ fontSize: 14 }}>📄</span> Header
                </button>
                <button onClick={openFooterModal} style={S.secBtn} title="Insert document footer">
                  <span style={{ fontSize: 14 }}>📋</span> Footer
                </button>
                <span style={{ width: 1, height: 20, background: '#CBD5E1', margin: '0 4px' }} />
                <button onClick={handlePageBreak} style={S.secBtn} title="Insert page break">
                  <span style={{ fontSize: 14 }}>⸻</span> Page Break
                </button>
                <button onClick={handleInsertTable} style={S.secBtn} title="Insert table">
                  <span style={{ fontSize: 14 }}>▦</span> Table
                </button>
                <span style={{ width: 1, height: 20, background: '#CBD5E1', margin: '0 4px' }} />
                <button onClick={handleInsertLogo} style={S.secBtn} title="Insert image/logo at cursor">
                  <span style={{ fontSize: 14 }}>🖼</span> Logo/Image
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Header / Footer Modal ── */}
      {hfModal && (
        <div className="lb-modal-overlay" onClick={() => setHfModal(null)}>
          <div className="lb-modal" onClick={e => e.stopPropagation()}>
            <h3>{hfModal === 'header' ? 'Insert Document Header' : 'Insert Document Footer'}</h3>

            {/* Logo upload */}
            <label>
              Company Logo (optional)
              <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 12 }}>
                {hfLogoData && (
                  <img src={hfLogoData} alt="Logo preview" style={{ width: parseInt(hfLogoSize) || 80, maxHeight: 80, objectFit: 'contain', border: '1px solid #e2e8f0', borderRadius: 4, padding: 4 }} />
                )}
                <button
                  onClick={() => logoInputRef.current?.click()}
                  style={{ padding: '6px 14px', border: '1px solid #CBD5E1', borderRadius: 6, background: '#f8fafc', cursor: 'pointer', fontSize: 12, color: '#334155' }}
                >
                  {hfLogoData ? 'Change Logo' : 'Upload Logo'}
                </button>
                {hfLogoData && (
                  <button
                    onClick={() => setHfLogoData(null)}
                    style={{ padding: '6px 10px', border: '1px solid #FCA5A5', borderRadius: 6, background: '#FEF2F2', cursor: 'pointer', fontSize: 12, color: '#DC2626' }}
                  >
                    Remove
                  </button>
                )}
                <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoFile} style={{ display: 'none' }} />
              </div>
            </label>

            {/* Logo size */}
            {hfLogoData && (
              <label>
                Logo Width (px)
                <input
                  type="number"
                  value={hfLogoSize}
                  onChange={e => setHfLogoSize(e.target.value)}
                  min="30" max="600" step="10"
                  style={{ maxWidth: 120 }}
                />
              </label>
            )}

            {/* Alignment */}
            <label>
              Alignment
              <select value={hfLogoAlign} onChange={e => setHfLogoAlign(e.target.value)}>
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </label>

            {/* Toggles */}
            <div style={{ display: 'flex', gap: 16, margin: '12px 0', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 0 }}>
                <input type="checkbox" checked={hfCompanyName} onChange={e => setHfCompanyName(e.target.checked)} />
                Company Name
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 0 }}>
                <input type="checkbox" checked={hfAddress} onChange={e => setHfAddress(e.target.checked)} />
                Address
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 0 }}>
                <input type="checkbox" checked={hfDate} onChange={e => setHfDate(e.target.checked)} />
                Date
              </label>
            </div>

            {/* Custom text */}
            <label>
              Custom Text (optional)
              <input
                type="text"
                value={hfCustomText}
                onChange={e => setHfCustomText(e.target.value)}
                placeholder="e.g. Phone: +91-xxx | Email: hr@company.com"
              />
            </label>

            <div className="btn-row">
              <button
                onClick={() => setHfModal(null)}
                style={{ background: '#f1f5f9', border: '1px solid #CBD5E1', color: '#475569' }}
              >
                Cancel
              </button>
              <button
                onClick={insertHeaderFooter}
                style={{ background: '#3B82F6', border: 'none', color: '#fff' }}
              >
                Insert {hfModal === 'header' ? 'Header' : 'Footer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
